/**
 * CR310: Universal Block Metadata
 * 
 * Single source of truth for block grouping, labels, and admin-only flags.
 * Used by Structure tab UI and debug inspector.
 */

export type BlockUIGroup = "core" | "value" | "actions" | "admin";
export type BlockSurface = "primary" | "details";

/**
 * CR316: Get block metadata for a specific surface
 */
export function getBlockMetadataForSurface(surface: BlockSurface): BlockMetadata[] {
  return surface === "primary" ? PRIMARY_BLOCK_METADATA : DETAILS_BLOCK_METADATA;
}

export interface BlockMetadata {
  /** Block ID - must match registry and variant definitions */
  id: string;
  /** UI group for Structure tab organization */
  uiGroup: BlockUIGroup;
  /** Default display label in Structure tab */
  defaultLabel: string;
  /** Surface this block belongs to */
  surface: BlockSurface;
  /** Whether this block is admin-only */
  adminOnly?: boolean;
  /** Optional link to labels.* key for dynamic titles */
  labelKey?: string;
  /** Per-deal-type label overrides for Structure tab display */
  labelOverridesByDealType?: Record<string, string>;
}

// ================================================================
// UNIVERSAL PRIMARY SURFACE BLOCKS
// These IDs MUST exist in every variant registry for true consistency
// ================================================================

export const UNIVERSAL_PRIMARY_BLOCK_IDS = [
  "header",
  "merchant_category_chips",
  "ribbon",
  "starburst",
  "meta_pills",
  "headline",
  "promo_badges",
  "details_pills",
  "value_box",
  "spend_box",
  "reward_box",
  "eligibility_box",
  "dates",
  "cta_button",
  "feedback",
  "details_cta", // CR309: Details trigger button
  "flyer_snippet", // CR332: Flyer snippet thumbnail
  "card_snippet", // CR481: Configurable card snippet
  "confidence_indicator",
  "debug_strip",
] as const;

export const PRIMARY_BLOCK_METADATA: BlockMetadata[] = [
  // Core Sections
  { id: "header", uiGroup: "core", defaultLabel: "Header", surface: "primary" },
  { id: "merchant_category_chips", uiGroup: "core", defaultLabel: "Merchant/Category Chips", surface: "primary" },
  { id: "ribbon", uiGroup: "core", defaultLabel: "Ribbon Banner", surface: "primary" },
  { id: "starburst", uiGroup: "core", defaultLabel: "Savings Indicator (legacy key: starburst)", surface: "primary" },
  { id: "meta_pills", uiGroup: "core", defaultLabel: "Meta Pills", surface: "primary" },
  { id: "headline", uiGroup: "core", defaultLabel: "Headline", surface: "primary" },
  { id: "promo_badges", uiGroup: "core", defaultLabel: "Promo Badges", surface: "primary" },
  { id: "details_pills", uiGroup: "core", defaultLabel: "Detail Pills", surface: "primary" },
  { id: "dates", uiGroup: "core", defaultLabel: "Validity Dates", surface: "primary" },
  
  // Value Boxes
  { 
    id: "value_box", 
    uiGroup: "value", 
    defaultLabel: "Effective Value Box", 
    surface: "primary",
    labelKey: "value_title",
    labelOverridesByDealType: {
      BONUS: "Effective Value Box",
      POINTS: "Cash Equivalent Box",
      DISCOUNT: "You Pay Box",
      IN_KIND: "Estimated Value Box",
      UNCLASSIFIED: "Value Box",
    },
  },
  { 
    id: "spend_box", 
    uiGroup: "value", 
    defaultLabel: "Qualifying Spend Box", 
    surface: "primary",
    labelKey: "qualifying_spend_title",
  },
  { 
    id: "reward_box", 
    uiGroup: "value", 
    defaultLabel: "Reward Box", 
    surface: "primary",
    labelKey: "reward_title",
    labelOverridesByDealType: {
      BONUS: "Bonus Value Box",
      POINTS: "Points Earned Box",
      DISCOUNT: "Discount Box",
      IN_KIND: "Free Item Box",
      UNCLASSIFIED: "Reward Box",
    },
  },
  { 
    id: "eligibility_box", 
    uiGroup: "value", 
    defaultLabel: "Eligibility Box", 
    surface: "primary",
    labelKey: "eligibility_title",
  },
  
  // Actions
  { id: "cta_button", uiGroup: "actions", defaultLabel: "CTA Button", surface: "primary" },
  { id: "feedback", uiGroup: "actions", defaultLabel: "Feedback", surface: "primary" },
  { id: "details_cta", uiGroup: "actions", defaultLabel: "Details CTA", surface: "primary" }, // CR309: Details trigger button
  
  // CR332: Flyer snippet
  { id: "flyer_snippet", uiGroup: "core", defaultLabel: "Flyer Snippet", surface: "primary" },
  // CR481: Card Snippet (configurable with eligibility + fallback)
  { id: "card_snippet", uiGroup: "core", defaultLabel: "Card Snippet", surface: "primary" },
  
  // Admin (formerly Extended)
  { id: "confidence_indicator", uiGroup: "admin", defaultLabel: "Confidence Indicator", surface: "primary", adminOnly: true },
  { id: "debug_strip", uiGroup: "admin", defaultLabel: "Debug Strip", surface: "primary", adminOnly: true },
];

// ================================================================
// UNIVERSAL DETAILS SURFACE BLOCKS
// These IDs MUST exist in every variant registry for true consistency
// ================================================================

export const UNIVERSAL_DETAILS_BLOCK_IDS = [
  "back_header",
  "back_nav",
  "eligibility",
  "terms",
  "fine_print",
  "source",
  "evidence",
  "validator",
  "details_actions",
  "flyer_snippet_details", // CR335: Flyer snippet on card back (admin-only)
] as const;

export const DETAILS_BLOCK_METADATA: BlockMetadata[] = [
  { id: "back_header", uiGroup: "core", defaultLabel: "Back Header", surface: "details" },
  { id: "back_nav", uiGroup: "core", defaultLabel: "Back Navigation", surface: "details" },
  { 
    id: "eligibility", 
    uiGroup: "core", 
    defaultLabel: "Eligibility", 
    surface: "details",
    labelKey: "eligibility_title",
  },
  { id: "terms", uiGroup: "core", defaultLabel: "Terms", surface: "details" },
  { id: "fine_print", uiGroup: "core", defaultLabel: "Fine Print", surface: "details" },
  { id: "source", uiGroup: "core", defaultLabel: "Source", surface: "details" },
  { id: "evidence", uiGroup: "admin", defaultLabel: "Evidence", surface: "details", adminOnly: true },
  { id: "validator", uiGroup: "admin", defaultLabel: "Validator", surface: "details", adminOnly: true },
  // CR335: Flyer snippet on card back (admin-only)
  { id: "flyer_snippet_details", uiGroup: "admin", defaultLabel: "Flyer Snippet", surface: "details", adminOnly: true },
  { id: "details_actions", uiGroup: "actions", defaultLabel: "Details Actions", surface: "details" },
];

// ================================================================
// COMBINED METADATA
// ================================================================

export const ALL_BLOCK_METADATA = [...PRIMARY_BLOCK_METADATA, ...DETAILS_BLOCK_METADATA];

// ================================================================
// HELPER FUNCTIONS
// ================================================================

/**
 * Get metadata for a specific block ID
 */
export function getBlockMetadata(blockId: string): BlockMetadata | undefined {
  return ALL_BLOCK_METADATA.find(m => m.id === blockId);
}

/**
 * Get all blocks for a specific UI group
 */
export function getBlocksByGroup(group: BlockUIGroup, surface?: BlockSurface): BlockMetadata[] {
  return ALL_BLOCK_METADATA.filter(m => 
    m.uiGroup === group && 
    (surface === undefined || m.surface === surface)
  );
}

/**
 * Get display label for a block, considering deal type overrides
 */
export function getBlockDisplayLabel(blockId: string, dealType?: string): string {
  const meta = getBlockMetadata(blockId);
  if (!meta) return blockId;
  
  if (dealType && meta.labelOverridesByDealType?.[dealType]) {
    return meta.labelOverridesByDealType[dealType];
  }
  
  return meta.defaultLabel;
}

/**
 * Get all primary surface block IDs (universal contract)
 */
export function getUniversalPrimaryBlockIds(): string[] {
  return PRIMARY_BLOCK_METADATA.map(m => m.id);
}

/**
 * Get all details surface block IDs (universal contract)
 */
export function getUniversalDetailsBlockIds(): string[] {
  return DETAILS_BLOCK_METADATA.map(m => m.id);
}

/**
 * Group blocks by UI group for Structure tab rendering
 */
export function groupBlocksByUIGroup(surface: BlockSurface): Record<BlockUIGroup, BlockMetadata[]> {
  const blocks = surface === "primary" ? PRIMARY_BLOCK_METADATA : DETAILS_BLOCK_METADATA;
  
  return {
    core: blocks.filter(b => b.uiGroup === "core"),
    value: blocks.filter(b => b.uiGroup === "value"),
    actions: blocks.filter(b => b.uiGroup === "actions"),
    admin: blocks.filter(b => b.uiGroup === "admin"),
  };
}

/**
 * Get the UI group display name
 */
export function getUIGroupDisplayName(group: BlockUIGroup): string {
  const names: Record<BlockUIGroup, string> = {
    core: "Core Sections",
    value: "Value Boxes",
    actions: "Actions",
    admin: "Admin",
  };
  return names[group];
}

// ================================================================
// CR315: UNIVERSAL BLOCK CONTRACT VALIDATION
// ================================================================

export interface BlockContractValidation {
  /** IDs present in registry */
  registryBlockIds: string[];
  /** Universal IDs missing from registry */
  missingUniversalBlocks: string[];
  /** Extra IDs not in universal contract (allowed but informational) */
  extraBlockIds: string[];
  /** Whether registry is fully compliant */
  isCompliant: boolean;
}

/**
 * Validate that a registry's block IDs include all universal blocks
 */
export function validateBlockContract(
  registryBlockIds: string[],
  surface: BlockSurface
): BlockContractValidation {
  const universalIds = surface === "primary" 
    ? [...UNIVERSAL_PRIMARY_BLOCK_IDS]
    : [...UNIVERSAL_DETAILS_BLOCK_IDS];
  
  const registrySet = new Set(registryBlockIds);
  const universalSet = new Set<string>(universalIds);
  
  const missingUniversalBlocks = universalIds.filter(id => !registrySet.has(id));
  const extraBlockIds = registryBlockIds.filter(id => !universalSet.has(id));
  
  return {
    registryBlockIds,
    missingUniversalBlocks,
    extraBlockIds,
    isCompliant: missingUniversalBlocks.length === 0,
  };
}

/**
 * Get missing universal block IDs for a surface
 */
export function getMissingUniversalBlocks(
  registryBlockIds: string[],
  surface: BlockSurface
): string[] {
  const universalIds = surface === "primary"
    ? [...UNIVERSAL_PRIMARY_BLOCK_IDS]
    : [...UNIVERSAL_DETAILS_BLOCK_IDS];
  
  const registrySet = new Set(registryBlockIds);
  return universalIds.filter(id => !registrySet.has(id));
}

/**
 * Check if a block ID is a universal block
 */
export function isUniversalBlockId(blockId: string, surface: BlockSurface): boolean {
  const universalIds = surface === "primary"
    ? UNIVERSAL_PRIMARY_BLOCK_IDS
    : UNIVERSAL_DETAILS_BLOCK_IDS;
  return (universalIds as readonly string[]).includes(blockId);
}
